import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Building2, MoreVertical, Plus, Search, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { ScoreNumber, SeverityChip } from '../components/evidence';
import { scoreBand } from '../lib/damage';

import { API } from '../lib/api';

const PROPERTY_TYPES = [
  ['apartment', 'Apartment'],
  ['house', 'House'],
  ['condo', 'Condo'],
  ['townhouse', 'Townhouse'],
  ['commercial', 'Commercial'],
];

const EMPTY_FORM = { name: '', address: '', property_type: 'apartment', unit_count: 1, notes: '' };

// Tenant is only shown when the property notes record one ("Tenant: Name").
function tenantFromNotes(property) {
  const match = String(property?.notes || '').match(/tenant\s*:\s*([^\n;]+)/i);
  return match?.[1]?.trim() || null;
}

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function typeName(type) {
  return PROPERTY_TYPES.find(([v]) => v === type)?.[1] || 'Property';
}

const th = 'whitespace-nowrap border-b border-line px-4 py-3 text-xs font-bold uppercase leading-4 tracking-[0.08em] text-ink-muted';
const td = 'border-b border-line px-4 py-3.5 align-middle';
const num = `${td} text-right font-mono font-semibold tabular`;

export default function PropertiesPage() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('highest_score');
  const [toDelete, setToDelete] = useState(null);

  const fetchProperties = async () => {
    try {
      const res = await axios.get(`${API}/properties`);
      setProperties(res.data);
    } catch {
      toast.error("Couldn't load your properties. Refresh to try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProperties(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/properties`, form);
      toast.success('Property added');
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      fetchProperties();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't add the property. Check the fields and try again.");
    } finally {
      setCreating(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await axios.delete(`${API}/properties/${toDelete.id}`);
      toast.success('Property deleted');
      fetchProperties();
    } catch {
      toast.error("Couldn't delete the property. Try again.");
    } finally {
      setToDelete(null);
    }
  };

  const visibleProperties = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const rows = properties.filter((p) => (
      !query ||
      String(p.name || '').toLowerCase().includes(query) ||
      String(p.address || '').toLowerCase().includes(query) ||
      String(tenantFromNotes(p) || '').toLowerCase().includes(query)
    ));
    return [...rows].sort((a, b) => {
      if (sortBy === 'lowest_score') return (a.avg_damage_score ?? 999) - (b.avg_damage_score ?? 999);
      if (sortBy === 'highest_score') return (b.avg_damage_score ?? -1) - (a.avg_damage_score ?? -1);
      if (sortBy === 'recent_inspection') {
        return new Date(b.last_inspection_date || 0).getTime() - new Date(a.last_inspection_date || 0).getTime();
      }
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }, [properties, searchQuery, sortBy]);

  const totalUnits = properties.reduce((sum, p) => sum + (Number(p.unit_count) || 0), 0);

  return (
    <DashboardLayout>
      <div data-testid="properties-page" className="flex flex-col gap-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="m-0 font-display text-3xl font-bold leading-9 tracking-[-0.01em]" data-testid="properties-heading">Properties</h1>
            <p className="m-0 mt-1 text-ink-muted">
              {properties.length} {properties.length === 1 ? 'property' : 'properties'}, {totalUnits} {totalUnits === 1 ? 'unit' : 'units'}
            </p>
          </div>
          <Button onClick={() => setDialogOpen(true)} data-testid="add-property-btn">
            <Plus strokeWidth={1.5} />Add property
          </Button>
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="relative min-w-0 flex-[1_1_320px]">
            <Search className="pointer-events-none absolute left-3 top-3 h-5 w-5 text-ink-muted" strokeWidth={1.5} aria-hidden="true" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by property, address or tenant"
              aria-label="Search properties"
              className="pl-10"
              data-testid="property-search-input"
            />
          </div>
          <div className="flex items-center gap-2.5">
            <Label htmlFor="property-sort">Sort</Label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger id="property-sort" className="w-[230px]" data-testid="property-sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="highest_score">Highest damage score</SelectItem>
                <SelectItem value="lowest_score">Lowest damage score</SelectItem>
                <SelectItem value="recent_inspection">Most recent inspection</SelectItem>
                <SelectItem value="name_az">Name, A to Z</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="h-[320px] rounded-md bg-sunken" aria-busy="true" aria-label="Loading properties" />
        ) : visibleProperties.length === 0 ? (
          <div className="flex flex-col items-start gap-3 rounded-md border border-dashed border-line-strong bg-surface p-8">
            <Building2 className="h-6 w-6 text-ink-muted" strokeWidth={1.5} aria-hidden="true" />
            <h2 className="m-0 font-display text-[22px] font-bold leading-7">
              {properties.length === 0 ? 'Add your first property' : 'No properties match your search'}
            </h2>
            <p className="m-0 max-w-[46ch] text-ink-muted">
              {properties.length === 0
                ? 'A property holds its rooms and every inspection you run there.'
                : 'Try a different name, address or tenant.'}
            </p>
            {properties.length === 0 && (
              <Button onClick={() => setDialogOpen(true)} data-testid="empty-add-property"><Plus strokeWidth={1.5} />Add property</Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-line bg-surface">
            <table className="w-full min-w-[900px] border-collapse text-left text-[15px] leading-[22px]">
              <thead>
                <tr>
                  <th scope="col" className={th}>Property</th>
                  <th scope="col" className={th}>Type</th>
                  <th scope="col" className={`${th} text-right`}>Units</th>
                  <th scope="col" className={`${th} text-right`}>Rooms</th>
                  <th scope="col" className={`${th} text-right`}>Inspections</th>
                  <th scope="col" className={th}>Damage score</th>
                  <th scope="col" className={th}>Last inspection</th>
                  <th scope="col" className={th}>Tenant</th>
                  <th scope="col" className={th}><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {visibleProperties.map((p) => {
                  const tenant = tenantFromNotes(p);
                  const last = formatDate(p.last_inspection_date);
                  return (
                    <tr key={p.id} className="hover:bg-sunken" data-testid={`property-card-${p.id}`}>
                      <td className={td}>
                        <Link to={`/properties/${p.id}`} className="font-bold text-ink no-underline hover:underline">{p.name}</Link>
                        <span className="block text-sm text-ink-muted">{p.address}</span>
                      </td>
                      <td className={td}>{typeName(p.property_type)}</td>
                      <td className={num}>{p.unit_count ?? 1}</td>
                      <td className={num}>{p.rooms?.length || 0}</td>
                      <td className={num}>{p.inspection_count || 0}</td>
                      <td className={td}>
                        {scoreBand(p.avg_damage_score) ? (
                          <span className="inline-flex items-center gap-2.5">
                            <ScoreNumber score={p.avg_damage_score} />
                            <SeverityChip score={p.avg_damage_score} />
                          </span>
                        ) : (
                          <span className="text-ink-muted"><span className="font-mono font-semibold">--</span> Not scored yet</span>
                        )}
                      </td>
                      <td className={`${td} font-mono text-sm font-semibold`}>{last || <span className="text-ink-muted">None yet</span>}</td>
                      <td className={td}>
                        {tenant || <span className="text-ink-muted">Not recorded</span>}
                      </td>
                      <td className={`${td} whitespace-nowrap text-right`}>
                        <Button asChild variant="outline" size="sm"><Link to={`/properties/${p.id}`}>Open</Link></Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="ml-1 w-9 px-0" aria-label={`More actions for ${p.name}`}>
                              <MoreVertical strokeWidth={1.5} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild><Link to={`/properties/${p.id}`}>Open property</Link></DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onSelect={() => setToDelete(p)}
                              className="text-sev-high focus:text-sev-high"
                              data-testid={`delete-property-${p.id}`}
                            >
                              <Trash2 strokeWidth={1.5} />Delete property
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Add property</DialogTitle>
            <DialogDescription>You'll add rooms after it's created.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="flex flex-col gap-5">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="property-name">Property name</Label>
              <Input
                id="property-name"
                data-testid="property-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Oakwood Apartments"
                aria-describedby="property-name-help"
                required
              />
              <p id="property-name-help" className="m-0 text-sm leading-5 text-ink-muted">The name your tenants know it by. It appears on every report.</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="property-address">Address</Label>
              <Input
                id="property-address"
                data-testid="property-address-input"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="1420 Bertrand Dr, Lafayette, LA 70506"
                required
              />
            </div>
            <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4">
              <div className="flex min-w-0 flex-col gap-1.5">
                <Label htmlFor="property-type">Type</Label>
                <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                  <SelectTrigger id="property-type" data-testid="property-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex min-w-0 flex-col gap-1.5">
                <Label htmlFor="property-units">Units</Label>
                <Input
                  id="property-units"
                  data-testid="property-units-input"
                  type="number"
                  min={1}
                  value={form.unit_count}
                  onChange={(e) => setForm({ ...form, unit_count: parseInt(e.target.value, 10) || 1 })}
                  className="font-mono"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={creating} data-testid="create-property-submit">
                {creating ? 'Adding…' : 'Add property'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this property?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.name}, with its rooms and {toDelete?.inspection_count || 0} {toDelete?.inspection_count === 1 ? 'inspection' : 'inspections'}, will be removed. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep property</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-sev-high text-surface hover:bg-sev-high hover:opacity-90">
              Delete property
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
